import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";
import { EditorDndProvider } from "../canvas/dnd/EditorDndProvider";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useBuilderToolbarDismiss } from "components/builderShell/hooks/useBuilderToolbarDismiss";
import {
  emailBuilderCloseBlockSettings,
  emailBuilderSetActivePanel,
} from "redux/actions/emailBuilder/emailBuilder";
import { useEditorActions, useEditorStore } from "../domain/redux/hooks";
import { selectPastCount } from "../domain/selectors";
import { useEmailBuilderCanvasHotkeys } from "./hooks/useEmailBuilderCanvasHotkeys";
import { useEmailBuilderClientShellHistoryHotkeys } from "./hooks/useEmailBuilderClientShellHistoryHotkeys";
import { useEmailBuilderClientShellSelectionSettingsSync } from "./hooks/useEmailBuilderClientShellSelectionSettingsSync";
import { useEmailBuilderEditorDocumentAutosaveSync } from "./hooks/useEmailBuilderEditorDocumentAutosaveSync";
import { EmailBuilderBanner } from "./banner/EmailBuilderBanner";
import { EmailBuilderToolbar } from "./toolbar/EmailBuilderToolbar";
import { EmailBuilderPanel } from "./panel/EmailBuilderPanel";
import { EmailBuilderCanvas } from "./canvas/EmailBuilderCanvas";
import { EmailBuilderCanvasLoadingPlaceholder } from "./EmailBuilderCanvasLoadingPlaceholder";
import { EmailBuilderPreview } from "./preview/EmailBuilderPreview";
import { useEmailBuilderPanelSlide } from "./hooks/useEmailBuilderPanelSlide";
import { useEmailBuilderMobileToolbarDrawer } from "./hooks/useEmailBuilderMobileToolbarDrawer";
import { useEmailBuilderDragCancelSink } from "./dnd/useEmailBuilderDragCancelSink";
import { shouldIgnoreEmailBuilderToolbarDismissTarget } from "./emailBuilderToolbarDismiss";
import { EMAIL_BUILDER_BLOCK_ANCHOR_ATTR } from "./canvas/settings/emailBuilderSettingsFocus";
import {
  emailBuilderAddElementsPanelAfterHotkey,
  resolveEmailBuilderAddElementsPanelHotkey,
} from "./emailBuilderPanelHotkeys";
import type { TEmailBuilderActivePanel } from "redux/reducers/emailBuilder/emailBuilder";
import { EmailBuilderDesignErrorBoundary } from "./EmailBuilderDesignErrorBoundary";
import { useEmailBuilderAddPanelCloseOnCanvasSelection } from "./hooks/useEmailBuilderAddPanelCloseOnCanvasSelection";
import { useEmailBuilderDesignEscapeKey } from "./hooks/useEmailBuilderDesignEscapeKey";
import { emailBuilderShouldCloseBlockSettingsForToolbarPanel } from "./emailBuilderCloseBlockSettingsForToolbarPanel";
import {
  EmailBuilderViewportProvider,
  useEmailBuilderViewport,
} from "./emailBuilderViewportContext";
import { EmailBuilderMobileToolsButton } from "./mobile/EmailBuilderMobileToolsButton";
import { EmailBuilderMobileDesignNudge } from "./mobile/EmailBuilderMobileDesignNudge";
import { EmailBuilderClientShellSettingsSurfaceHost } from "./EmailBuilderClientShellSettingsSurfaceHost";
import { EmailBuilderAutosaveConflictModal } from "./EmailBuilderAutosaveConflictModal";
import { EmailBuilderUnavailableMediaModal } from "./EmailBuilderUnavailableMediaModal";
import { emailBuilderReloadFromConflict } from "../emailBuilderReloadFromConflict";
import { EmailBuilderConfirmProvider } from "./EmailBuilderConfirmProvider";
import "./emailBuilderShellLayout.scss";

interface IEmailBuilderShellProps {
  onBack: () => void;
  canvasLoading?: boolean;
  editorSyncReady?: boolean;
}

function EmailBuilderShellContent(props: IEmailBuilderShellProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const mode = useAppSelector(store => store.emailBuilderStore.mode);
  const autoSaveConflict = useAppSelector(store => store.emailBuilderStore.autoSaveConflict);
  const unavailableLibraryMediaRepairedCount = useAppSelector(
    store => store.emailBuilderStore.unavailableLibraryMediaRepairedCount,
  );
  const workspace = useAppSelector(store => store.emailBuilderStore.workspace);
  const editorSyncReady = props.editorSyncReady === true;
  const editorPastCount = useEditorStore(selectPastCount);
  const { undo: editorUndo, setSelection } = useEditorActions();
  const canUndoDesign = editorPastCount > 0;
  useEmailBuilderClientShellHistoryHotkeys(mode);
  useEmailBuilderCanvasHotkeys(mode);
  useEmailBuilderClientShellSelectionSettingsSync(mode);
  useEmailBuilderEditorDocumentAutosaveSync(editorSyncReady);
  const activePanel = useAppSelector(store => store.emailBuilderStore.activePanel);
  const editingMode = useAppSelector(store => store.emailBuilderStore.editingMode);
  const { usesBottomSheetChrome, usesDesktopRightSettingsPanel } = useEmailBuilderViewport();

  const [mobileChromeReady, setMobileChromeReady] = useState(false);
  const [reloadingFromConflict, setReloadingFromConflict] = useState(false);
  const [unavailableMediaNoticeDismissed, setUnavailableMediaNoticeDismissed] =
    useState(false);
  const mobileChromeInitializedRef = useRef(false);
  const previousModeRef = useRef(mode);

  useEffect(() => {
    setUnavailableMediaNoticeDismissed(false);
  }, [workspace?.id, workspace?.kind]);

  useEffect(() => {
    const previousMode = previousModeRef.current;
    previousModeRef.current = mode;

    if (previousMode === "design" && mode === "preview") {
      dispatch(emailBuilderCloseBlockSettings());
      setSelection({ kind: "email" });
    }
  }, [dispatch, mode, setSelection]);

  useLayoutEffect(() => {
    if (mobileChromeInitializedRef.current) {
      return;
    }
    mobileChromeInitializedRef.current = true;

    if (activePanel !== "none") {
      dispatch(emailBuilderSetActivePanel("none"));
    }
    setMobileChromeReady(true);
  }, [activePanel, dispatch]);
  const { panelOpen, closingPanel, closingFromPanel, setActivePanel } = useEmailBuilderPanelSlide();
  useEmailBuilderAddPanelCloseOnCanvasSelection({ setActivePanel });
  const cancelSinkRef = useEmailBuilderDragCancelSink();
  const toolbarRef = useRef<HTMLDivElement>(null);

  const handleReloadFromConflict = useCallback(() => {
    if (workspace === null || reloadingFromConflict) {
      return;
    }

    setReloadingFromConflict(true);
    void emailBuilderReloadFromConflict(dispatch, workspace).finally(() => {
      setReloadingFromConflict(false);
    });
  }, [dispatch, reloadingFromConflict, workspace]);
  const panelRef = useRef<HTMLDivElement>(null);
  const mobileToolsButtonRef = useRef<HTMLButtonElement>(null);

  const closeBlockSettingsBeforeToolbarPanel = useCallback(
    (panel: TEmailBuilderActivePanel) => {
      if (
        !emailBuilderShouldCloseBlockSettingsForToolbarPanel(panel, editingMode)
      ) {
        return;
      }

      dispatch(emailBuilderCloseBlockSettings());
      if (usesDesktopRightSettingsPanel) {
        setSelection({ kind: "email" });
      }
    },
    [dispatch, editingMode, setSelection, usesDesktopRightSettingsPanel],
  );

  const setActivePanelGuarded = useCallback(
    (panel: TEmailBuilderActivePanel) => {
      closeBlockSettingsBeforeToolbarPanel(panel);
      setActivePanel(panel);
    },
    [closeBlockSettingsBeforeToolbarPanel, setActivePanel],
  );

  const finishMobilePanel = useCallback(() => {
    setActivePanelGuarded("none");
  }, [setActivePanelGuarded]);

  const {
    toolbarOpen: mobileToolbarOpen,
    toggleTools: toggleMobileTools,
    closeToolbar: closeMobileToolbar,
    closePanel: closeMobilePanel,
    dismissPanelThenToolbar: dismissMobileToolbarAfterInsert,
    selectNavItem: mobileToolbarSelectNavItem,
  } = useEmailBuilderMobileToolbarDrawer({
    enabled: usesBottomSheetChrome,
    mode,
    activePanel,
    editingMode,
    onBeforeOpen: () => {
      closeBlockSettingsBeforeToolbarPanel("add-elements");
    },
    onCloseToolbar: finishMobilePanel,
    onClosePanel: finishMobilePanel,
    onSelectPanel: panel => {
      setActivePanelGuarded(panel);
    },
  });

  const shouldIgnoreDismissTarget = useCallback(
    (target: Node) => {
      if (shouldIgnoreEmailBuilderToolbarDismissTarget(target)) {
        return true;
      }

      if (
        (activePanel !== "none" || mobileToolbarOpen) &&
        target instanceof Element &&
        target.closest(`[${EMAIL_BUILDER_BLOCK_ANCHOR_ATTR}]`)
      ) {
        return true;
      }

      return false;
    },
    [activePanel, mobileToolbarOpen],
  );

  const dismissToolbarPanel = useCallback(() => {
    if (usesBottomSheetChrome) {
      closeMobileToolbar();
      return;
    }

    setActivePanelGuarded("none");
  }, [closeMobileToolbar, setActivePanelGuarded, usesBottomSheetChrome]);

  const handleMoreMenuOpenChange = useCallback(
    (open: boolean) => {
      if (!open || mode !== "design") {
        return;
      }

      if (usesDesktopRightSettingsPanel && editingMode === "block-settings") {
        dispatch(emailBuilderCloseBlockSettings());
        setSelection({ kind: "email" });
      }

      dismissToolbarPanel();
    },
    [dismissToolbarPanel, dispatch, editingMode, mode, setSelection, usesDesktopRightSettingsPanel],
  );

  const toolbarDismissOpen = usesBottomSheetChrome
    ? mode === "design" && mobileToolbarOpen
    : mode === "design" && activePanel !== "none";

  useBuilderToolbarDismiss({
    isOpen: toolbarDismissOpen,
    onClose: dismissToolbarPanel,
    railRef: usesBottomSheetChrome ? mobileToolsButtonRef : toolbarRef,
    panelRef: panelRef,
    shouldIgnoreTarget: shouldIgnoreDismissTarget,
    disabled: mode !== "design",
    dismissOnEscape: false,
  });

  useEmailBuilderDesignEscapeKey({
    mode,
    activePanel,
    mobileToolbarOpen,
    dismissToolbarPanel,
  });

  useEffect(() => {
    if (mode !== "design") {
      return;
    }

    function handleKeydown(event: KeyboardEvent) {
      if (resolveEmailBuilderAddElementsPanelHotkey(event) !== "toggle-add-elements") {
        return;
      }

      event.preventDefault();
      if (usesBottomSheetChrome) {
        const nextPanel = emailBuilderAddElementsPanelAfterHotkey(activePanel);
        if (mobileToolbarOpen) {
          closeMobileToolbar();
          return;
        }

        if (nextPanel !== "none") {
          mobileToolbarSelectNavItem(nextPanel);
        }
        return;
      }

      setActivePanelGuarded(emailBuilderAddElementsPanelAfterHotkey(activePanel));
    }

    window.document.addEventListener("keydown", handleKeydown, true);
    return () => window.document.removeEventListener("keydown", handleKeydown, true);
  }, [
    activePanel,
    closeMobileToolbar,
    mobileToolbarOpen,
    mobileToolbarSelectNavItem,
    mode,
    setActivePanelGuarded,
    usesBottomSheetChrome,
  ]);

  const showMobileToolsButton = usesBottomSheetChrome && mode === "design";
  const canvasLoading = props.canvasLoading === true;

  return (
    <div
      ref={cancelSinkRef}
      className={classNames("email-builder-shell", {
        "email-builder-shell--preview": mode === "preview",
        "email-builder-shell--mobile-design": showMobileToolsButton,
        "email-builder-shell--canvas-loading": canvasLoading,
      })}
    >
      <a className="email-builder-skip-link" href="#email-builder-main">
        {t("Skip to email canvas")}
      </a>
      <EmailBuilderBanner onBack={props.onBack} onMoreMenuOpenChange={handleMoreMenuOpenChange} />
      <EmailBuilderMobileDesignNudge visible={showMobileToolsButton} />
      <div className="email-builder-shell-body">
        <EmailBuilderToolbar
          ref={toolbarRef}
          closingPanel={closingPanel}
          setActivePanel={setActivePanelGuarded}
        />
        <EmailBuilderPanel
          ref={panelRef}
          panelOpen={panelOpen && mobileChromeReady}
          closingPanel={closingPanel}
          closingFromPanel={closingFromPanel}
          setActivePanel={setActivePanelGuarded}
          mobileToolbarOpen={mobileToolbarOpen && mobileChromeReady}
          mobileChromeReady={mobileChromeReady}
          onMobileToolbarClose={closeMobileToolbar}
          onMobilePanelClose={closeMobilePanel}
          onMobileDismissAfterInsert={dismissMobileToolbarAfterInsert}
          onMobileToolbarSelectNavItem={mobileToolbarSelectNavItem}
        />
        <main
          id="email-builder-main"
          className="email-builder-shell-canvas-host"
          tabIndex={-1}
          aria-busy={canvasLoading}
          aria-label={mode === "preview" ? t("Email preview") : t("Email canvas")}
        >
          <EmailBuilderDesignErrorBoundary
            resetKey={`${editorPastCount}:${mode}`}
            canUndo={canUndoDesign}
            onUndo={() => {
              editorUndo();
            }}
          >
            {canvasLoading ? (
              <EmailBuilderCanvasLoadingPlaceholder />
            ) : mode === "preview" ? (
              <EmailBuilderPreview />
            ) : (
              <EmailBuilderCanvas />
            )}
          </EmailBuilderDesignErrorBoundary>
        </main>
      </div>
      {showMobileToolsButton ? (
        <EmailBuilderMobileToolsButton
          ref={mobileToolsButtonRef}
          open={mobileToolbarOpen}
          onClick={toggleMobileTools}
          ariaLabel={t("Builder tools")}
        />
      ) : null}
      <EmailBuilderClientShellSettingsSurfaceHost mode={mode} />
      <EmailBuilderAutosaveConflictModal
        open={autoSaveConflict}
        reloading={reloadingFromConflict}
        onReload={handleReloadFromConflict}
      />
      <EmailBuilderUnavailableMediaModal
        open={
          editorSyncReady &&
          unavailableLibraryMediaRepairedCount > 0 &&
          !unavailableMediaNoticeDismissed
        }
        repairedCount={unavailableLibraryMediaRepairedCount}
        onClose={() => setUnavailableMediaNoticeDismissed(true)}
      />
    </div>
  );
}

export function EmailBuilderShell(props: IEmailBuilderShellProps) {
  return (
    <EmailBuilderConfirmProvider>
      <EditorDndProvider>
        <EmailBuilderViewportProvider>
          <EmailBuilderShellContent {...props} />
        </EmailBuilderViewportProvider>
      </EditorDndProvider>
    </EmailBuilderConfirmProvider>
  );
}
